import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    HostListener
} from "@angular/core";
import { toBoolean } from "@logex/framework/utilities";
import { LgSwitchMode } from "./lg-switch/lg-switch.types";

/**
 * Visual color mode of {@link LgSliderSwitchComponent}. A narrowing of {@link LgSwitchMode} - the
 * slider switch has no `positive` styling, but the members it does share carry the same meaning,
 * so an `LgSwitchMode` value can be passed straight through.
 * - `neutral`: original behavior - off = gray, on = blue.
 * - `negative`: off state is rendered in the error (red) color instead of gray.
 */
export type LgSliderSwitchMode = Extract<LgSwitchMode, "neutral" | "negative">;

@Component({
    selector: "lg-slider-switch",
    template: `
        <div class="lg-slider-switch__slide"></div>
        <div class="lg-slider-switch__handle"></div>
    `,
    host: {
        class: "lg-slider-switch",
        "[class.lg-slider-switch--on]": "state",
        "[class.lg-slider-switch--off]": "!state",
        "[class.lg-slider-switch--disabled]": "disabled",
        "[class.lg-slider-switch--negative]": "mode === 'negative'"
    },
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LgSliderSwitchComponent {
    /**
     * Slider state.
     *
     * @default false
     * `true` - on, `false` - off
     */
    @Input({ required: true }) public set state(value: boolean) {
        this._state = toBoolean(value);
    }

    public get state(): boolean {
        return this._state;
    }

    @Input() public set disabled(value: boolean) {
        this._disabled = toBoolean(value);
    }

    public get disabled(): boolean {
        return this._disabled;
    }

    /**
     * Visual color mode of the switch.
     * - `neutral` (default): original behavior - off = gray, on = blue.
     * - `negative`: off state is rendered in the error (red) color instead of gray. The on state
     *   is left unchanged (blue), so it can be used to visualize e.g. an "exclude" toggle.
     *
     * @default "neutral"
     */
    @Input() public mode: LgSliderSwitchMode = "neutral";

    @Output() public readonly stateChange = new EventEmitter<boolean>();

    @HostListener("click", ["$event"])
    public _clicked($event: MouseEvent): void {
        $event.stopPropagation();
        $event.preventDefault();
        if (this._disabled) return;

        this._state = !this._state;
        this.stateChange.next(this._state);
    }

    private _state = false;
    private _disabled = false;
}
